/**
 * Twin heat-events resource (material-genealogy-real C3) — typed wrapper over
 * the chronological replay surface: the ordered stream of events one heat
 * emitted as it walked the plant (charge → melt → tap → pour-per-casting →
 * cooling → shakeout → QC → dispatch).
 *
 * The new-model emitter (BE-B `genealogy_newmodel.go`) derives these events
 * deterministically and persists them into `twin_batches.trace.events[]`; the
 * replay API (BE-C `replay.go`) serves them at
 * `GET /v1/twin/heats/{heat_id}/events`. The ReplayPlayer (FE-B) scrubs over
 * `events[].t`; the live overlay (FE-D) reads the latest per-node event.
 *
 * Wire shapes mirror the Go `HeatEvent` / `HeatEventStream` structs exactly.
 * `params` reuses the same {name,value,unit,inSpec} chip shape the trace +
 * batch surfaces use, so the param-chip renderer is shared.
 *
 * NOTE: **`entity_instance_id` === `PlantNode.id`** — every event names the
 * entity instance it occurred at via that join key (see model-gate.ts).
 */

import { api } from "@/lib/api-client";

/** One chip-shaped captured parameter on an event (in-spec aware). */
export interface HeatEventParam {
  name: string;
  value: number | string;
  unit?: string;
  /** Whether the value sits within its declared spec range. */
  inSpec?: boolean;
}

/**
 * A single chronological event in a heat's life. `kind` is an open string
 * (e.g. "charge", "melt_start", "tap", "pour", "cooling", "shakeout", "qc",
 * "dispatch", "alarm") — kept loose so the emitter can add kinds without a FE
 * change. `entity_instance_id` (== `PlantNode.id`) maps the event to a node on
 * the process graph. `seq` is a stable monotonic order within the stream.
 */
export interface HeatEvent {
  /** ISO timestamp the event occurred at. */
  t: string;
  /** Monotonic sequence number within the stream (stable tiebreak for equal `t`). */
  seq: number;
  /** Event kind — open vocabulary (charge / melt_start / tap / pour / qc / …). */
  kind: string;
  /** The entity instance this event occurred at — **== `PlantNode.id`**. */
  entity_instance_id: string;
  /** Heat this event belongs to (human-readable number). */
  heat_number?: string;
  /** Part produced by this event, when it emits a tracked unit (e.g. a casting). */
  part_number?: string;
  /** Batch / sub-batch label, when applicable. */
  batch_number?: string;
  /** Mass moved / produced by this event (mass-conservation roll-up). */
  mass?: { value: number; unit: string };
  /** Captured process parameters at this event (pour temp, moisture, …). */
  params?: HeatEventParam[];
  /** Alarm raised by this event, when `kind === "alarm"` (or any alarming step). */
  alarm?: {
    severity: "info" | "warning" | "critical";
    message: string;
  };
}

/** The full ordered event stream for one heat (the replay payload). */
export interface HeatEventStream {
  heat_id: string;
  heat_number: string;
  /** ISO timestamp of the first event (replay clock start). */
  from: string;
  /** ISO timestamp of the last event (replay clock end). */
  to: string;
  /** Events in chronological (`t`, then `seq`) order. */
  events: HeatEvent[];
}

export const twinHeatEvents = {
  /**
   * The chronological event stream one heat emitted (material-genealogy-real
   * C3). New-model only; legacy heats have no events row. 404 until the
   * replay endpoint deploys — the caller (self-gated query) tolerates that.
   */
  stream: (heatId: string) =>
    api.get<HeatEventStream>(
      `/v1/twin/heats/${encodeURIComponent(heatId)}/events`,
    ),
};
